import { useUIStore, Theme } from '@/store/uiStore';
import { Sun, Moon, Laptop } from 'lucide-react';
import { clsx } from 'clsx';

interface ThemeToggleProps {
    compact?: boolean;
    className?: string;
}

export function ThemeToggle({ compact = false, className }: ThemeToggleProps) {
    const { theme, setTheme } = useUIStore();

    const cycleTheme = () => {
        if (theme === 'light') setTheme('dark');
        else if (theme === 'dark') setTheme('system');
        else setTheme('light');
    };

    if (compact) {
        return (
            <button
                type="button"
                onClick={cycleTheme}
                title={`Current theme: ${theme}. Click to switch.`}
                className={clsx(
                    'p-2 rounded-xl text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors',
                    className
                )}
            >
                {theme === 'light' && <Sun className="w-4 h-4 text-amber-500" />}
                {theme === 'dark' && <Moon className="w-4 h-4 text-teal-400" />}
                {theme === 'system' && <Laptop className="w-4 h-4 text-slate-400" />}
            </button>
        );
    }

    return (
        <div className={clsx('flex items-center p-1 bg-slate-100 dark:bg-slate-800 rounded-xl', className)}>
            <button
                type="button"
                onClick={() => setTheme('light')}
                title="Light Mode"
                className={clsx(
                    'flex-1 flex items-center justify-center gap-1.5 py-1 px-2 rounded-lg text-xs font-semibold transition-all',
                    theme === 'light'
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'
                )}
            >
                <Sun className={clsx('w-3.5 h-3.5', theme === 'light' && 'text-amber-500')} />
                <span className="text-[11px]">Light</span>
            </button>

            <button
                type="button"
                onClick={() => setTheme('dark')}
                title="Dark Mode"
                className={clsx(
                    'flex-1 flex items-center justify-center gap-1.5 py-1 px-2 rounded-lg text-xs font-semibold transition-all',
                    theme === 'dark'
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'
                )}
            >
                <Moon className={clsx('w-3.5 h-3.5', theme === 'dark' && 'text-teal-400')} />
                <span className="text-[11px]">Dark</span>
            </button>

            <button
                type="button"
                onClick={() => setTheme('system')}
                title="System Mode"
                className={clsx(
                    'flex-1 flex items-center justify-center gap-1.5 py-1 px-2 rounded-lg text-xs font-semibold transition-all',
                    theme === 'system'
                        ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'
                )}
            >
                <Laptop className="w-3.5 h-3.5" />
                <span className="text-[11px]">Auto</span>
            </button>
        </div>
    );
}
